import React, { Fragment } from "react"
import PropTypes from "prop-types"
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table"
import { Table, Row, Col, Button, Input } from "reactstrap"

// Lightweight, self-contained client-side table (search + sort + pagination).
// The ERP proxy returns the full customer list in one call, so client-side
// paging is enough here — no server-side pagination props needed.
const ErpClientsTable = ({ columns, data, customPageSize = 15 }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: customPageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  return (
    <Fragment>
      <Row className="mb-2">
        <Col md={4}>
          <Input
            type="text"
            value={globalFilter || ""}
            onChange={e => setGlobalFilter(e.target.value || undefined)}
            placeholder={`Search ${data.length} clients...`}
          />
        </Col>
      </Row>

      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()}>
          <thead className="table-light table-center">
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th key={column.id} {...column.getSortByToggleProps()}>
                    {column.render("Header")}
                    {column.isSorted ? (
                      <i
                        className={
                          column.isSortedDesc
                            ? " bx bx-sort-down"
                            : " bx bx-sort-up"
                        }
                      />
                    ) : (
                      ""
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map(row => {
                prepareRow(row)
                return (
                  <tr key={row.id} {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td
                        key={cell.column.id}
                        {...cell.getCellProps()}
                        data-label={cell?.column?.Header}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="react-bs-table-no-data p-5 text-center"
                >
                  Sorry, There are no data to display
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {data.length > 0 && (
        <Row className="justify-content-end align-items-center g-2">
          <Col className="col-md-auto">
            <select
              className="form-select"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 30, 40, 50].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </Col>
          <Col className="col-md-auto">
            <span>
              Page{" "}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </span>
          </Col>
          <Col className="col-md-auto">
            <div className="d-flex gap-1">
              <Button
                color="primary"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {"<<"}
              </Button>
              <Button
                color="primary"
                onClick={previousPage}
                disabled={!canPreviousPage}
              >
                {"<"}
              </Button>
              <Button
                color="primary"
                onClick={nextPage}
                disabled={!canNextPage}
              >
                {">"}
              </Button>
              <Button
                color="primary"
                onClick={() => gotoPage(pageOptions.length - 1)}
                disabled={!canNextPage}
              >
                {">>"}
              </Button>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  )
}

ErpClientsTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  customPageSize: PropTypes.number,
}

export default ErpClientsTable
